// services/chatConversations.ts
//
// Conversation history for the AI companion (ORA-458). The turn itself streams
// over `POST /api/v5/chat/turn`; everything a student does *around* a turn —
// see the threads they have, reopen one, delete one — goes through here.
//
// THE CONTRACT THIS CLIENT IS WRITTEN AGAINST — ora-be's ORA-458 half must
// match it, or bump it and tell this file:
//
//   GET /api/v5/chat/conversations?skip=<n>
//   200 { conversations: [{ id, title, lastMessageAt, legacy }], hasMore }
//
//   GET /api/v5/chat/conversations/:id
//   200 { conversation: {
//           id, title, legacy,
//           messages: [{ id, role, text, attachments?, citations?, blocks?,
//                       followups?, activity? }],
//           legacyToolBlocks?: PersistedToolBlock[]   // legacy conversations only
//        } }
//   404 { error: 'not_found' }
//
//   GET /api/v5/chat/conversations/anchor?type=generic
//   GET /api/v5/chat/conversations/anchor?type=question&id=<vignette>&variantId=<variant>
//   GET /api/v5/chat/conversations/anchor?type=qbank_block&id=<block> (ORA-1285)
//   200 { conversation: <the detail above> | null, found: boolean }
//
//   `POST /api/v5/chat/turn` accepts `forceNewConversation: true` only for
//   the first turn after New Chat. The envelope anchor keys that new row;
//   ordinary first turns resume the current anchor's newest non-empty thread.
//
//   attachments: [{ id, url, mimeType, key?, sizeBytes? }] — a PRESIGNED url
//   (one hour), plus optional storage metadata from newer backend deploys.
//   Omitted entirely for a message with none, and omitted for a message whose
//   signing failed, so a restored image is never something to rely on.
//
//   DELETE /api/v5/chat/conversations/:id  → 200 { success: true } | 404
//
//   POST /api/v5/chat/conversations/:id/blocks          (ORA-514, no model call)
//   body: { messageId: string; citationId: string }
//   200 { message: <a message exactly as the GET above returns one, with
//                   blocks[] and no text> }
//   400 the citation is not addable · 404 message/citation not in this
//   conversation · idempotent: an already-added citation returns the existing
//   block message with 200.
//
// Notes on the shape, and why:
//
// - Blocks come back two ways because they were written two ways. A turn the
//   agent runtime streamed has its served blocks rebuilt server-side into the
//   block contract (`types/chatBlocks.ts`) and arrives on the message. A turn
//   the old pipeline wrote has no such record; what survives is the panel's
//   persisted UI state, so those arrive raw as `legacyToolBlocks` and
//   `useChatConversations` maps them with the mapper ORA-448 already owns.
// - Reads are parsed defensively — a message missing its blocks, a block
//   missing its target — because half a conversation is better than an error
//   screen over history the student can see is there.
// - Nothing here dispatches. The parsed conversation is handed to
//   `conversationHydrated` by the hook, so this file stays a transport.

import axiosInstance from './axiosCreator';
import {
  asArray,
  asNonEmptyString,
  parseChatConversationDetail,
  parseMessage,
  parseSummary,
  record,
  type ChatConversationDetail,
  type ChatConversationMessage,
  type ChatConversationPage,
  type ChatConversationSummary,
} from './chatConversationParsing';

export { parseChatConversationDetail } from './chatConversationParsing';
export type {
  ChatConversationBlock,
  ChatConversationDetail,
  ChatConversationMessage,
  ChatConversationPage,
  ChatConversationSummary,
  ChatRestoredAttachment,
} from './chatConversationParsing';

export type ChatConversationAnchor =
  | { type: 'generic' }
  /** A partner-course module's one continuous thread (ORA-1055). */
  | { type: 'course_module'; id: string }
  /** A QBank block's one thread, when the student keeps one (ORA-1285). */
  | { type: 'qbank_block'; id: string }
  | {
      type: 'question' | 'flashcard' | 'video' | 'article' | 'lecture' | 'exam';
      id: string;
      variantId?: string;
    };

export const CHAT_CONVERSATIONS_ROUTE = '/api/v5/chat/conversations';

/** One page of the student's conversations, newest first. */
export const fetchChatConversations = async (
  params?: { skip?: number; search?: string },
  signal?: AbortSignal
): Promise<ChatConversationPage> => {
  const response = await axiosInstance.get(CHAT_CONVERSATIONS_ROUTE, {
    params:
      params?.skip || params?.search
        ? {
            ...(params.skip ? { skip: params.skip } : {}),
            ...(params.search ? { search: params.search } : {}),
          }
        : undefined,
    signal,
  });

  const raw = record(response.data) ?? {};

  return {
    conversations: asArray(raw.conversations)
      .map(parseSummary)
      .filter((item): item is ChatConversationSummary => item !== null),
    hasMore: raw.hasMore === true,
  };
};

/**
 * One conversation with its messages. Returns null on a 404 — a conversation
 * the student deleted in another tab is not an error, it is gone.
 */
export const fetchChatConversation = async (
  conversationId: string,
  signal?: AbortSignal
): Promise<ChatConversationDetail | null> => {
  try {
    const response = await axiosInstance.get(
      `${CHAT_CONVERSATIONS_ROUTE}/${encodeURIComponent(conversationId)}`,
      { signal }
    );

    return parseChatConversationDetail(record(response.data)?.conversation);
  } catch (error) {
    if (httpStatus(error) === 404) return null;
    throw error;
  }
};

/**
 * Read the latest non-empty conversation for an anchor. This endpoint never
 * creates rows; the first turn does, after the student actually sends it.
 */
export const fetchChatConversationForAnchor = async (
  anchor: ChatConversationAnchor,
  signal?: AbortSignal
): Promise<ChatConversationDetail | null> => {
  try {
    const response = await axiosInstance.get(
      `${CHAT_CONVERSATIONS_ROUTE}/anchor`,
      {
        params: anchor,
        signal,
      }
    );

    return parseChatConversationDetail(record(response.data)?.conversation);
  } catch (error) {
    // A backend from before ORA-1285 refuses the block anchor as an invalid
    // one (400): it has no block threads, so there is none to find.
    if (anchor.type === 'qbank_block' && httpStatus(error) === 400) return null;
    throw error;
  }
};

const httpStatus = (error: unknown): number | null => {
  const status = (error as { response?: { status?: unknown } })?.response
    ?.status;

  return typeof status === 'number' ? status : null;
};

/**
 * Delete one conversation. A 404 resolves rather than throws: the server
 * saying it is not there is the outcome the student asked for.
 */
export const deleteChatConversation = async (
  conversationId: string
): Promise<void> => {
  try {
    await axiosInstance.delete(
      `${CHAT_CONVERSATIONS_ROUTE}/${encodeURIComponent(conversationId)}`
    );
  } catch (error) {
    if (httpStatus(error) === 404) return;
    throw error;
  }
};

/** The mutable metadata the conversation sidebar exposes. */
export type ChatConversationUpdate = { title: string } | { pinned: boolean };

/** The minimal acknowledgement returned after a metadata change. */
export interface UpdatedChatConversation {
  id: string;
  title: string | null;
  pinnedAt: string | null;
}

/**
 * Rename or pin one conversation. The server remains the source of truth for
 * `pinnedAt`, so a client clock can never make a locally pinned row sort ahead
 * of a newer server pin.
 */
export const updateChatConversation = async (
  conversationId: string,
  update: ChatConversationUpdate
): Promise<UpdatedChatConversation> => {
  const response = await axiosInstance.patch(
    `${CHAT_CONVERSATIONS_ROUTE}/${encodeURIComponent(conversationId)}`,
    update
  );

  const raw = record(record(response.data)?.conversation);
  const id = asNonEmptyString(raw?.id);
  if (!raw || !id) {
    throw new Error('Malformed chat conversation update response');
  }

  return {
    id,
    title: asNonEmptyString(raw.title),
    pinnedAt: asNonEmptyString(raw.pinnedAt),
  };
};

/** The add-as-block route for one conversation (ORA-514). */
export const chatConversationBlocksRoute = (conversationId: string): string =>
  `${CHAT_CONVERSATIONS_ROUTE}/${encodeURIComponent(conversationId)}/blocks`;

/**
 * What a badge on a served question block points at, rather than cites
 * (ORA-546/547): the linked flashcards the missed question assigned, or the
 * QBank variant that miss scheduled. The server resolves the content from the
 * student's own rows; the id is all the client supplies.
 */
export type ChatAddBlockReference =
  | { kind: 'linked_flashcards' }
  | { kind: 'scheduled_variant'; variantId: string };

/**
 * The two ways to name what to add: one of the answer's citations, or what one
 * of its served question blocks points at.
 */
export type ChatAddBlockBody =
  | { messageId: string; citationId: string }
  | {
      messageId: string;
      blockId: string;
      reference: ChatAddBlockReference;
    };

/**
 * Add an item to the conversation as a block, with no model call (ORA-514,
 * extended by ORA-546/547). The server appends one block-only assistant
 * message after `messageId` and returns it in the same shape the conversation
 * read uses, so the caller hands it straight to the slice. Adding the same
 * thing twice returns the first block rather than a second one.
 *
 * Throws on a refused or malformed response: the control that asked re-enables
 * itself and says so, and nothing is appended to the thread.
 */
export const addChatConversationBlock = async (
  conversationId: string,
  body: ChatAddBlockBody
): Promise<ChatConversationMessage> => {
  const response = await axiosInstance.post(
    chatConversationBlocksRoute(conversationId),
    body
  );

  const message = parseMessage(record(response.data)?.message);
  if (!message) throw new Error('Malformed add-block response');

  return message;
};
